/**
 * Token Manager
 * 
 * Gerencia o armazenamento e recuperação do token de autenticação
 * usando tanto localStorage quanto cookies compartilhados entre subdomínios.
 * 
 * Isso permite que maker.myplat.net.br e app.myplat.net.br compartilhem
 * o mesmo token de autenticação e dados de tenant.
 */

const TOKEN_KEY = 'authToken';
const COOKIE_NAME = 'myplat_auth_token';
const TENANT_ID_KEY = 'tenantId';
const TENANT_LABEL_KEY = 'tenantLabel';
// Nomes alinhados com o resto do MyPlat (myplat-react-web-vite, myplat-sso-web-vite,
// myplat-web-bpo) — antes eram "myplat_tenant_id/_label" (sem "_app_"), inofensivo pra
// esse app já que o Maker não depende do SSO pra selecionar tenant, mas evita confusão.
const TENANT_ID_COOKIE = 'myplat_app_tenant_id';
const TENANT_LABEL_COOKIE = 'myplat_app_tenant_label';
const MAKER_VERSION_KEY = 'makerVersion';
const SHARED_DOMAIN = '.myplat.net.br';

// Configuração de chunking para tokens grandes
const CHUNK_SIZE = 3000; // 3KB por chunk (limite seguro para cookies)
const CHUNK_COOKIE_PREFIX = 'myplat_auth_token_chunk';
const CHUNK_COUNT_COOKIE = 'myplat_auth_token_chunks';

/**
 * Define um cookie com os atributos de segurança apropriados
 * @param {string} name - Nome do cookie
 * @param {string} value - Valor do cookie
 * @param {number} days - Dias até expiração (padrão: 7 dias)
 */
function setCookie(name, value, days = 7) {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;

  // Detecta se está em ambiente de desenvolvimento (localhost ou HTTP)
  const isDevelopment = window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.protocol === 'http:';

  // Em produção (HTTPS), usa Secure; em desenvolvimento, não usa
  const secureFlag = isDevelopment ? '' : 'Secure;';

  // Define o domínio apenas se não for localhost
  const domainFlag = isDevelopment ? '' : `domain=${SHARED_DOMAIN};`;

  document.cookie = `${name}=${value}; ${expires}; path=/; ${domainFlag} ${secureFlag} SameSite=Lax`;
}

/**
 * Recupera o valor de um cookie pelo nome
 * @param {string} name - Nome do cookie
 * @returns {string|null} - Valor do cookie ou null se não encontrado
 */
function getCookie(name) {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i].trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length);
    }
  }
  return null;
}

/**
 * Remove um cookie
 * @param {string} name - Nome do cookie
 */
function deleteCookie(name) {
  const isDevelopment = window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.protocol === 'http:';

  const domainFlag = isDevelopment ? '' : `domain=${SHARED_DOMAIN};`;

  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; ${domainFlag}`;
}

/**
 * Salva um token grande dividindo-o em chunks (pedaços) se necessário
 * @param {string} token - Token a ser salvo
 * @param {number} days - Dias até expiração
 */
function setTokenWithChunking(token, days = 7) {
  if (!token) {
    console.warn('setTokenWithChunking: token vazio');
    return;
  }
  // Limpa chunks antigos antes de salvar novos
  deleteTokenChunks();

  // Se o token couber em um único cookie, salva direto
  if (token.length <= CHUNK_SIZE) {
    setCookie(COOKIE_NAME, token, days);
    return;
  }

  // Token grande - dividir em chunks
  const chunks = [];
  for (let i = 0; i < token.length; i += CHUNK_SIZE) {
    chunks.push(token.substring(i, i + CHUNK_SIZE));
  }

  // Salva cada chunk
  chunks.forEach((chunk, index) => {
    setCookie(`${CHUNK_COOKIE_PREFIX}${index}`, chunk, days);
  });

  // Salva o contador de chunks
  setCookie(CHUNK_COUNT_COOKIE, chunks.length.toString(), days);
}

/**
 * Recupera um token que pode estar dividido em chunks
 * @returns {string|null} - Token completo ou null
 */
function getTokenFromChunks() {
  // Verifica se existe cookie de chunk count
  const chunkCountStr = getCookie(CHUNK_COUNT_COOKIE);

  // Se não há chunks, tenta cookie único
  if (!chunkCountStr) {
    return getCookie(COOKIE_NAME);
  }

  // Reconstrói o token a partir dos chunks
  const chunkCount = parseInt(chunkCountStr, 10);
  if (isNaN(chunkCount) || chunkCount <= 0) {
    console.warn('Chunk count inválido:', chunkCountStr);
    return null;
  }

  let token = '';
  for (let i = 0; i < chunkCount; i++) {
    const chunk = getCookie(`${CHUNK_COOKIE_PREFIX}${i}`);
    if (!chunk) {
      console.warn(`Chunk ${i} não encontrado`);
      return null;
    }
    token += chunk;
  }

  return token;
}

/**
 * Remove todos os chunks de token
 */
function deleteTokenChunks() {
  // Remove cookie único se existir
  deleteCookie(COOKIE_NAME);

  // Verifica quantos chunks existem
  const chunkCountStr = getCookie(CHUNK_COUNT_COOKIE);
  if (!chunkCountStr) {
    return; // Não há chunks para remover
  }

  const chunkCount = parseInt(chunkCountStr, 10);
  if (isNaN(chunkCount) || chunkCount <= 0) {
    return;
  }

  // Remove cada chunk
  for (let i = 0; i < chunkCount; i++) {
    deleteCookie(`${CHUNK_COOKIE_PREFIX}${i}`);
  }

  // Remove o contador
  deleteCookie(CHUNK_COUNT_COOKIE);

}

/**
 * Token Manager - API pública
 */
const tokenManager = {
  /**
   * Salva o token tanto no localStorage quanto no cookie compartilhado
   * Usa chunking automático para tokens grandes (> 3KB)
   * @param {string} token - Token de autenticação
   */
  setToken(token) {
    if (!token) {
      console.warn('tokenManager.setToken: token vazio ou inválido');
      return;
    }

    try {
      // Salva no localStorage (compatibilidade com código existente)
      localStorage.setItem(TOKEN_KEY, token);

      // Salva no cookie compartilhado (com chunking automático)
      setTokenWithChunking(token);

      // token saved
    } catch (error) {
      console.error('Erro ao salvar token:', error);
    }
  },

  /**
   * Recupera o token, priorizando o cookie compartilhado
   * Reconstrói automaticamente tokens divididos em chunks
   * @returns {string|null} - Token de autenticação ou null
   */
  getToken() {
    try {
      // Prioriza o cookie (fonte compartilhada entre subdomínios)
      // Tenta recuperar de chunks primeiro
      let token = getTokenFromChunks();

      // Fallback para localStorage se cookie não existir
      if (!token) {
        token = localStorage.getItem(TOKEN_KEY);

        // Se encontrou no localStorage mas não no cookie, sincroniza
        if (token) {
          // synchronize to cookie
        }
      }

      return token;
    } catch (error) {
      console.error('Erro ao recuperar token:', error);
      return null;
    }
  },

  /**
   * Remove o token de ambas as fontes (localStorage e cookie)
   * Remove também todos os chunks se existirem
   */
  removeToken() {
    try {
      // Remove do localStorage
      localStorage.removeItem(TOKEN_KEY);

      // Remove do cookie (incluindo todos os chunks)
      deleteTokenChunks();

      // Remove também os dados de tenant
      this.removeTenantData();

      // token removed
    } catch (error) {
      console.error('Erro ao remover token:', error);
    }
  },

  /**
   * Salva os dados do tenant (tenantId e tenantLabel) em localStorage e cookies
   * @param {string} tenantId - ID do tenant
   * @param {string} tenantLabel - Label/nome do tenant
   */
  setTenantData(tenantId, tenantLabel) {
    if (!tenantId || !tenantLabel) {
      console.warn('tokenManager.setTenantData: tenantId ou tenantLabel vazio');
      return;
    }

    try {
      // Salva no localStorage
      localStorage.setItem(TENANT_ID_KEY, tenantId);
      localStorage.setItem(TENANT_LABEL_KEY, tenantLabel);

      // Salva nos cookies compartilhados
      setCookie(TENANT_ID_COOKIE, tenantId);
      setCookie(TENANT_LABEL_COOKIE, tenantLabel);

      // tenant data saved
    } catch (error) {
      console.error('Erro ao salvar dados de tenant:', error);
    }
  },

  /**
   * Recupera os dados do tenant, priorizando os cookies
   * @returns {Object|null} - Objeto com tenantId e tenantLabel ou null
   */
  getTenantData() {
    try {
      // Prioriza os cookies (fonte compartilhada)
      let tenantId = getCookie(TENANT_ID_COOKIE);
      let tenantLabel = getCookie(TENANT_LABEL_COOKIE);

      // Fallback para localStorage se cookies não existirem
      if (!tenantId || !tenantLabel) {
        tenantId = localStorage.getItem(TENANT_ID_KEY);
        tenantLabel = localStorage.getItem(TENANT_LABEL_KEY);

        // Se encontrou no localStorage mas não nos cookies, sincroniza
        if (tenantId && tenantLabel) {
          // tenant data synchronized from localStorage to cookies
          setCookie(TENANT_ID_COOKIE, tenantId);
          setCookie(TENANT_LABEL_COOKIE, tenantLabel);
        }
      }

      // Retorna null se não encontrou nenhum dado
      if (!tenantId || !tenantLabel) {
        return null;
      }

      return { tenantId, tenantLabel };
    } catch (error) {
      console.error('Erro ao recuperar dados de tenant:', error);
      return null;
    }
  },

  /**
   * Remove os dados do tenant de ambas as fontes
   */
  removeTenantData() {
    try {
      // Remove do localStorage
      localStorage.removeItem(TENANT_ID_KEY);
      localStorage.removeItem(TENANT_LABEL_KEY);

      // Remove dos cookies
      deleteCookie(TENANT_ID_COOKIE);
      deleteCookie(TENANT_LABEL_COOKIE);

      // tenant data removed
    } catch (error) {
      console.error('Erro ao remover dados de tenant:', error);
    }
  },

  /**
   * Sincroniza o token entre localStorage e cookie
   * Útil para garantir consistência após mudanças manuais
   * Usa chunking automático ao sincronizar
   */
  syncToken() {
    try {
      const cookieToken = getTokenFromChunks();
      const localToken = localStorage.getItem(TOKEN_KEY);

      // Se ambos existem e são diferentes, prioriza o cookie
      if (cookieToken && localToken && cookieToken !== localToken) {
        // tokens differ - prefer cookie and sync to localStorage
        localStorage.setItem(TOKEN_KEY, cookieToken);
      }
      // Se só existe no localStorage, copia para cookie
      else if (!cookieToken && localToken) {
        setTokenWithChunking(localToken);
      }
      // Se só existe no cookie, copia para localStorage
      else if (cookieToken && !localToken) {
        localStorage.setItem(TOKEN_KEY, cookieToken);
      }
    } catch (error) {
      console.error('Erro ao sincronizar token:', error);
    }
  }
};

export default tokenManager;
